<template>
    <div class="details-page" v-if="invoiceDetails != null">
        <div class="md-layout">
            <div class="md-layout-item">
                <div class="custom-toolbar">
                    <div class="custom-toolbar-start">
                        <h3 class="title">
                            {{ invoiceDetails.invoiceNumber }}
                        </h3>
                    </div>
                    <div class="custom-toolbar-end" v-if="!$root.isMobileOnly"></div>
                </div>
            </div>
        </div>
        <div class="md-layout md-alignment-top-left content-layout-panel">
            <div
                class="md-layout-item md-small-size-100 md-medium-size-100 md-layout md-large-size-100 md-xlarge-size-60"
            >
                <div class="md-layout-item md-large-size-50 md-medium-size-100 md-small-size-100 md-xlarge-size-100">
                    <md-card>
                        <md-card-header class="md-card-header-icon md-card-header-blue">
                            <div class="card-icon">
                                <md-icon>description</md-icon>
                            </div>

                            <div v-if="invoiceDetails.invoiceBatchFromDraft && invoiceDetails.status !== 'Draft'"
                                class="approve-invoice-button"
                            >
                                <div class="approved-indicator">
                                    <md-icon class="approved-icon">check_circle</md-icon>
                                    <span class="approved-text">Approved</span>
                                </div>
                            </div>

                            <div v-if="invoiceDetails.status === 'Draft'" class="approve-container">
                                <approve-invoice-button
                                    v-if="!isReadOnlyUser"
                                    :invoice-id="invoiceDetails.teamMemberInvoiceId"
                                    @invoiceApproved="handleApproveInvoice"
                                    :invoice-status="invoiceDetails.status"
                                />
                            </div>
                        </md-card-header>

                        <md-card-content>
                            <div>
                                <div class="details-box" v-if="invoiceDetails.accountingSystemRef">
                                    <label>Accounting System Reference:</label>
                                    <span>
                                        <a
                                            target="_blank"
                                            :href="
                                                `https://go.xero.com/AccountsReceivable/Edit.aspx?InvoiceID=${
                                                    invoiceDetails.accountingSystemRef
                                                }`
                                            "
                                        >
                                            Open invoice in {{ invoiceDetails.accountingSystemName }}
                                        </a>
                                    </span>
                                </div>
                                <div class="details-box">
                                    <label>PO Number</label>
                                    <span>
                                        {{ invoiceDetails.purchaseOrderNumber }}
                                    </span>
                                </div>
                                <div class="details-box">
                                    <label>Team Member</label>
                                    <span>
                                        {{ invoiceDetails.teamMember.fullName }}
                                    </span>
                                </div>
                                <div class="details-box">
                                    <label>Invoice Date</label>
                                    <span>
                                        {{ invoiceDetails.invoiceDate | dateFormat(DATE_TYPES.standardDate) }}
                                    </span>
                                </div>
                                <div class="details-box">
                                    <label>Due Date</label>
                                    <span>
                                        {{ invoiceDetails.dueDate | dateFormat(DATE_TYPES.standardDate) }}
                                    </span>
                                </div>
                                <div class="details-box">
                                    <label>Status</label>
                                    <span>
                                        {{ invoiceDetails.status }}
                                    </span>
                                </div>
                            </div>
                        </md-card-content>
                    </md-card>
                </div>

                <div
                    v-if="canCreateIssue"
                    v-feature="FEATURE_NAMES.IssueTracking"
                    class="md-layout-item md-large-size-50 md-medium-size-100 md-small-size-100 md-xlarge-size-100"
                >
                    <review-invoice-issues
                        :issues="invoiceDetails.teamMemberInvoiceIssues"
                        :entity-ref="invoiceDetails.invoiceNumber"
                        :entity-id="invoiceDetails.teamMemberInvoiceId"
                        entity-type="TeamMemberInvoice"
                        :invoice-status="invoiceDetails.status"
                        @invoiceApproved="handleApproveInvoice"
                    />
                </div>

                <div class="md-layout-item md-large-size-100 md-medium-size-100 md-small-size-100 md-xlarge-size-100">
                    <invoice-lines :lines="invoiceDetails.invoiceLines" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { handleRequests } from '@/helpers';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { ReviewInvoiceIssues } from '@/components';
import { FEATURE_NAMES } from '@/utils/constants';
import { InvoiceLines } from './components';
import { ApproveInvoiceButton } from './buttons';

export default {
    name: 'TeamMemberInvoicesDetails',
    components: {
        InvoiceLines,
        ReviewInvoiceIssues,
        ApproveInvoiceButton
    },
    mixins: [GeneralMixin],
    async mounted() {
        this.$_handleLoaderState(true);
        await this.getTeamMemberInvoiceDetails();
        this.$_handleLoaderState(false);

        if (this.approveAction && this.invoiceDetails.status !== 'Draft') {
            const param = 'action';
            const query = { ...this.$route.query };
            delete query[param];

            this.$router.replace({ query });
        }
    },
    data() {
        return {
            invoiceDetails: null,
            FEATURE_NAMES,
            approveAction: this.$route.query.action?.toLowerCase() === 'approve'
        };
    },
    computed: {
        ...mapGetters({
            isReadOnlyUser: 'user/isReadOnlyUser',
            canCreateIssue: 'user/canCreateIssue'
        })
    },
    methods: {
        async getTeamMemberInvoiceDetails() {
            const api = `/api/team-member-invoices/${this.$route.params.invoiceId}`;
            const { data } = await handleRequests(api);
            this.invoiceDetails = data;
        },
        handleApproveInvoice() {
            this.invoiceDetails.status = 'Approved';
        }
    }
};
</script>

<style lang="scss" scoped>
.details-page {
    padding-top: 40px;

    ::v-deep .md-table-row:hover {
        background-color: #fafafa;
        cursor: pointer;
    }

    ::v-deep .md-field.md-disabled:after {
        border-bottom: none;
    }

    ::v-deep .md-field:after {
        height: 0;
    }

    ::v-deep .md-field:before {
        background-color: initial !important;
    }
}

::v-deep .md-card-header-icon {
    z-index: 9;
}

.custom-toolbar {
    .title {
        margin-bottom: 0;
    }
}

.content-layout-panel {
    > div {
        padding: 0;
    }
}

@media (min-width: 768px) {
    .details-page {
        padding-top: 0;
    }
}

.approve-container {
    max-width: 25px;
    max-height: 25px;
    float: right;
    margin: 10px 25px 25px 25px;
}

.details-columns {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
}

.details-column {
    flex: 1 1 50%;
    min-width: 320px;
}
@media (max-width: 767px) {
    .details-columns {
        flex-direction: column;
        gap: 0;
    }
    .details-column {
        flex: 1 1 100%;
        min-width: 0;
        margin-bottom: 16px;
    }
}

.approve-invoice-button {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin: 10px 25px 25px 25px;
}

.approved-indicator {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 999px;
    background-color: rgba(76, 175, 80, 1);
    color: white;
    font-weight: 600;
}

.approved-icon {
    margin-right: 0.25rem;
    color: white !important;
    
}

.approved-text {
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.75rem;
}
</style>
